import React, { Suspense } from 'react';
import HeaderMenu from './HeaderMenu.js';
import Footer from './Footer.js';
import Home from './Home.js';
import FadeIn from 'react-fade-in';

import './css/App.css';
import { Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';

// Hero images for preloading — only the most likely next pages, not all 7
// (With WebP images at ~100KB each, this is ~300KB total instead of ~700KB)
import {
  Hero02livetv,
  Hero04books,
  Hero06about,
} from './Images.js';
import useHeroImagePreloader from './hooks/useHeroImagePreloader';

// Route-based code splitting — only the visited page's code is downloaded
const LiveTV = React.lazy(() => import('./LiveTV.js'));
const Podcasts = React.lazy(() => import('./Podcasts.js'));
const Books = React.lazy(() => import('./Books.js'));
const Articles = React.lazy(() => import('./Articles.js'));
const About = React.lazy(() => import('./About.js'));
const Contact = React.lazy(() => import('./Contact.js'));
const NotFound = React.lazy(() => import('./NotFound.js'));

// LazyBookRoute: lazy-loads both BookData and BookCard on demand (only on /books routes)
function LazyBookRoute({ bookKey, extraKey }) {
  const [bookData, setBookData] = React.useState(null);
  const [BookCardComp, setBookCardComp] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let isCancelled = false;

    // reset state when bookKey changes
    setError(null);
    setBookData(null);
    setBookCardComp(null);

    Promise.all([
      import('./BookData'),
      import('./BookCard.js')
    ])
      .then(([dataModule, cardModule]) => {
        if (isCancelled) return;
        setBookData(dataModule[bookKey]);
        setBookCardComp(() => cardModule.default);
      })
      .catch((err) => {
        if (isCancelled) return;
        setError(err);
      });

    return () => {
      isCancelled = true;
    };
  }, [bookKey]);

  if (error) {
    // Let the nearest ErrorBoundary handle dynamic import failures
    throw error;
  }
  if (!bookData || !BookCardComp) {
    return <div style={{ minHeight: '400px' }} />;
  }

  return (
    <FadeIn>
      <BookCardComp
        bookimage={bookData.image}
        booktitle={bookData.title}
        booksubtitle={bookData.subtitle}
        bookdescription={bookData.desc}
        booklinks={bookData.links}
        bookextra={extraKey ? bookData.extra : undefined}
      />
    </FadeIn>
  );
}

// Simple loading placeholder that doesn't cause layout shift
const PageLoading = () => (
  <div style={{ minHeight: '80vh', backgroundColor: '#000' }} />
);

function App() {
  // Preload the 3 most likely next-page hero images (not all 7)
  // Home hero is already loaded as the LCP element on the landing page
  useHeroImagePreloader([
    Hero02livetv,
    Hero04books,
    Hero06about,
  ]);

  return (
      <div className="mainbody">
        <a href="#main-content" className="sr-only skip-link">Skip to main content</a>
        <HeaderMenu />
        <main id="main-content">
          <ErrorBoundary>
            <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/livetv" element={<LiveTV />} />
                <Route path="/podcasts" element={<Podcasts />} />
                <Route path="/books" element={<Books />}>
                  <Route path="/books" element={<LazyBookRoute bookKey="BookOne" />} />
                  <Route path="/books/bookone" element={<LazyBookRoute bookKey="BookOne" />} />
                  <Route path="/books/booktwo" element={<LazyBookRoute bookKey="BookTwo" />} />
                  <Route path="/books/bookthree" element={<LazyBookRoute bookKey="BookThree" />} />
                  <Route path="/books/bookfour" element={<LazyBookRoute bookKey="BookFour" extraKey />} />
                  <Route path="/books/bookfive" element={<LazyBookRoute bookKey="BookFive" extraKey />} />
                  <Route path="/books/booksix" element={<LazyBookRoute bookKey="BookSix" extraKey />} />
                  <Route path="/books/bookseven" element={<LazyBookRoute bookKey="BookSeven" extraKey />} />
                  <Route path="/books/bookeight" element={<LazyBookRoute bookKey="BookEight" extraKey />} />
                </Route>
                <Route path="/articles" element={<Articles />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
  );
}

export default App;
